/** Resolve Vibe chat deliverable kind + static report URLs for the right-hand panel. */

import { resolveReportFetchUrl } from '@/lib/reportHtml';

const ABAP_MARKERS = ['REPORT ', 'PROGRAM ', 'DATA:', 'FORM ', 'METHOD ', 'INCLUDE ', 'SELECT '];

export function looksLikeAbapSource(text) {
  if (!text || text.length < 400) return false;
  return ABAP_MARKERS.some((m) => text.includes(m));
}

const REPORT_FILENAME_RE = /sap_odata_report_\d{8}_\d{6}\.html/i;

/** Resolve a report filename from assistant text when artifact_url was not persisted. */
export function extractReportFilenameFromText(text) {
  const s = String(text || '');
  if (!s.trim()) return '';
  const direct = s.match(REPORT_FILENAME_RE);
  if (direct) return direct[0];
  const saved = s.match(/saved to\s+[^\s]*\/(sap_odata_report_[^\s]+\.html)/i);
  if (saved) return saved[1];
  return '';
}

export function extractReportUrlFromText(text) {
  const s = String(text || '');
  if (!s.trim()) return '';

  const labelMatch = s.match(/report link:\s*(https?:\/\/[^\s]+)/i);
  if (labelMatch) {
    return labelMatch[1].replace(/[.,)]+$/g, '');
  }

  const downloadMatch = s.match(/\*\*download link:\*\*\s*(https?:\/\/[^\s]+)/i);
  if (downloadMatch) {
    return downloadMatch[1].replace(/[.,)]+$/g, '');
  }

  const urls = s.match(/https?:\/\/[^\s)\]"']+/g);
  if (urls?.length) {
    const reportPath = urls.find((u) => /\/reports\//i.test(u));
    const chosen = reportPath || urls[urls.length - 1];
    return chosen.replace(/[.,)]+$/g, '');
  }

  const filename = extractReportFilenameFromText(s);
  if (filename) {
    return `https://local.invalid/reports/${filename}`;
  }
  return '';
}

export function isSecurityDeliverable(kind) {
  const k = String(kind || '').toLowerCase();
  return k === 'security' || k === 'sap_auth';
}

export function isWorkflowDeliverable(kind) {
  const k = String(kind || '').toLowerCase();
  return k === 'workflow' || k === 'nup' || k === 'new_user_provisioning';
}

export function resolveDeliverableKind(intent, artifactType, generatedCode = null) {
  const typeNorm = String(artifactType || '').toLowerCase();
  if (isSecurityDeliverable(typeNorm)) {
    return 'security';
  }
  if (typeNorm === 'static_report') {
    return 'static_report';
  }
  if (isWorkflowDeliverable(typeNorm)) {
    return 'workflow';
  }

  const intentNorm = String(intent || '').toLowerCase();
  if (isSecurityDeliverable(intentNorm)) {
    return 'security';
  }
  if (intentNorm === 'static_report') {
    return 'static_report';
  }
  if (isWorkflowDeliverable(intentNorm)) {
    return 'workflow';
  }

  const code = String(generatedCode || '').trim();
  if (code) {
    const url = extractReportUrlFromText(code);
    if (url && (/report link:/i.test(code) || !looksLikeAbapSource(code))) {
      return 'static_report';
    }
  }

  return 'abap';
}

const FALLBACK_INTENT_UI = {
  abap: {
    id: 'abap',
    label: 'ABAP program',
    short_label: 'ABAP',
    requires_connected: false,
    skip_confirm: false,
    supports_deploy: true,
    supports_prd: true,
    artifact_type: 'abap',
    generating_connected: 'Generating ABAP (with SAP validation)…',
    generating_disconnected: 'Generating ABAP…',
    revise_generating: 'Revising ABAP…',
  },
  security: {
    id: 'security',
    label: 'Security request',
    short_label: 'Security',
    requires_connected: true,
    skip_confirm: false,
    supports_deploy: false,
    supports_prd: true,
    artifact_type: 'security',
    generating_connected: 'Running Security Agent…',
    revise_generating: 'Updating security results…',
  },
  static_report: {
    id: 'static_report',
    label: 'Static HTML report',
    short_label: 'Static report',
    requires_connected: true,
    skip_confirm: true,
    supports_deploy: false,
    supports_prd: false,
    artifact_type: 'static_report',
    generating_connected: 'Generating static report…',
    revise_generating: 'Updating static report…',
  },
  workflow: {
    id: 'workflow',
    label: 'New User Provisioning',
    short_label: 'NUP',
    requires_connected: true,
    skip_confirm: true,
    supports_deploy: false,
    supports_prd: false,
    artifact_type: 'workflow',
    generating_connected: 'Running New User Provisioning…',
    revise_generating: 'Updating New User Provisioning…',
  },
};

/** Server-provided intent metadata from GET/POST chat status (with local fallback). */
export function resolveIntentUiMeta(intent, intentMeta = null) {
  if (intentMeta && typeof intentMeta === 'object' && intentMeta.id) {
    return intentMeta;
  }
  const kind = resolveDeliverableKind(intent, intentMeta?.artifact_type);
  return FALLBACK_INTENT_UI[kind] || FALLBACK_INTENT_UI.abap;
}

export function deliverableLabelFromMeta(meta) {
  return meta?.label || 'This request';
}

export function intentRequiresConnected(meta) {
  return Boolean(meta?.requires_connected);
}

export function resolveReportUrl(artifactUrl, generatedCode) {
  const direct = String(artifactUrl || '').trim();
  if (direct) return direct;
  return extractReportUrlFromText(generatedCode);
}

/** Normalize API meta into panel-ready intent / type / url fields. */
export function mergeDeliverableMeta(meta = {}) {
  const intent = meta.intent ? String(meta.intent) : null;
  let artifactType = meta.artifactType ? String(meta.artifactType) : null;
  if (!artifactType) {
    const intentNorm = String(intent || '').toLowerCase();
    if (intentNorm === 'static_report') {
      artifactType = 'static_report';
    } else if (isSecurityDeliverable(intentNorm)) {
      artifactType = 'security';
    } else if (isWorkflowDeliverable(intentNorm)) {
      artifactType = 'workflow';
    }
  }

  let artifactUrl = meta.artifactUrl ? String(meta.artifactUrl) : null;
  const generatedCode = meta.generatedCode ?? null;
  const artifactSummary = meta.artifactSummary ?? null;
  if (!artifactUrl && (artifactType === 'static_report' || intent === 'static_report')) {
    const extracted = extractReportUrlFromText(generatedCode);
    if (extracted) artifactUrl = extracted;
  }

  return { intent, artifactType, artifactUrl, generatedCode, artifactSummary };
}

export function resolveCopyPayload(kind, generatedCode, artifactUrl) {
  const code = String(generatedCode || '').trim();
  const url = resolveReportUrl(artifactUrl, generatedCode);
  if (kind === 'static_report' && url) {
    return resolveReportFetchUrl(url) || url;
  }
  return code;
}
